import React, { Component } from 'react';
import { LayoutsTable } from './LayoutsTable';
import { TableButton } from '../TableButton';
import { LoadingSpinner } from '../LoadingSpinner';

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid #e8e8e8',
    padding: '6px 0 4px 0',
    marginTop: '16px',
    cursor: 'pointer',
    userSelect: 'none'
};

const sectionLabelStyle = {
    fontSize: '11px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#aaa'
};

const chevronStyle = {
    fontSize: '10px',
    color: '#bbb',
    marginRight: '5px',
    display: 'inline-block',
    width: '10px'
};

const tintStyle = {
    background: 'rgba(155, 206, 230, 0.15)',
    borderRadius: '3px'
};

export class LayoutFoldersPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            openFolders: new Set(),
            dragOverId: null
        };
    }

    componentDidUpdate(prevProps) {
        const filterChanged = prevProps.table.filter !== this.props.table.filter;
        const layoutsChanged = prevProps.layouts !== this.props.layouts;

        if (this.props.table.filter !== "") {
            // Active search: keep matching folders expanded as data streams in or filter changes.
            if (filterChanged || layoutsChanged) {
                this.updateOpenFoldersBasedOnFilteredLayouts(this.props);
            }
        } else if (filterChanged) {
            // Filter was just cleared by the user - collapse back to default.
            this.updateOpenFoldersBasedOnFilteredLayouts(this.props);
        }
    }

    updateOpenFoldersBasedOnFilteredLayouts(props) {
        const filter = props.table.filter;
        if (filter === "") {
            if (this.state.openFolders.size > 0) {
                this.setState({ openFolders: new Set() });
            }
            return;
        }

        // Already filtered
        const layouts = props.layouts;

        const newOpen = new Set();
        layouts.forEach(layout => {
            const folderId = layout.folderId !== undefined && layout.folderId !== null ? layout.folderId : null;
            if (folderId !== null) {
                newOpen.add(folderId);
            }
        });

        // Only set state if there are changes
        const prev = this.state.openFolders;
        let changed = false;
        if (prev.size !== newOpen.size) {
            changed = true;
        } else {
            for (let id of newOpen) {
                if (!prev.has(id)) { changed = true; break; }
            }
        }

        if (changed) {
            this.setState({ openFolders: newOpen });
        }
    }

    toggleFolder(id) {
        var open = new Set(this.state.openFolders);
        if (open.has(id)) {
            open.delete(id);
        } else {
            open.add(id);
        }
        this.setState({ openFolders: open });
    }

    render() {
        var openFolders = this.state.openFolders;
        var dragOverId = this.state.dragOverId;
        var props = this.props;
        var folders = props.folders;
        var layouts = props.layouts;
        var selectedLayoutIds = props.selectedLayoutIds;
        var table = props.table;
        var isFoldersLoading = props.isFoldersLoading;
        var loadingGroups = props.loadingGroups || new Set();
        var onRowClick = props.onRowClick;
        var onDragStart = props.onDragStart;
        var onDropToFolder = props.onDropToFolder;
        var onEditLayout = props.onEditLayout;
        var onDeleteLayout = props.onDeleteLayout;
        var onCopyLayout = props.onCopyLayout;
        var onChangePage = props.onChangePage;
        var onChangeElementsPerPage = props.onChangeElementsPerPage;
        var onRenameFolder = props.onRenameFolder;
        var onDeleteFolder = props.onDeleteFolder;

        var grouped = {};
        folders.forEach(function(f) { grouped[f.id] = []; });
        var unassigned = [];
        layouts.forEach(function(layout) {
            if (layout.folderId && grouped[layout.folderId] !== undefined) {
                grouped[layout.folderId].push(layout);
            } else {
                unassigned.push(layout);
            }
        });

        var hasFolders = folders.length > 0;

        var makeTableProps = function(layoutsForGroup) {
            return {
                table: Object.assign({}, table, { data: layoutsForGroup, filter: "" }),
                appName: props.appName,
                // Only ever called once the section's own loading gate has passed, so its data is ready.
                isLoading: false,
                selectedLayoutIds: selectedLayoutIds,
                onRowClick: onRowClick,
                onDragStart: onDragStart,
                onEditLayout: onEditLayout,
                onDeleteLayout: onDeleteLayout,
                onCopyLayout: onCopyLayout,
                onChangePage: onChangePage,
                onChangeElementsPerPage: onChangeElementsPerPage
            };
        };

        var self = this;

        return (
            <div style={{ padding: '0 15px' }}>

                {hasFolders ? (
                    <div
                        style={Object.assign(
                            { minHeight: '24px' },
                            dragOverId === 'unassigned' ? tintStyle : {}
                        )}
                        onDragOver={function(e) { e.preventDefault(); self.setState({ dragOverId: 'unassigned' }); }}
                        onDragLeave={function(e) {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                                self.setState({ dragOverId: null });
                            }
                        }}
                        onDrop={function(e) {
                            e.preventDefault();
                            self.setState({ dragOverId: null });
                            onDropToFolder && onDropToFolder(e, null);
                        }}
                    >
                        {(isFoldersLoading || loadingGroups.has('unassigned'))
                            ? <LoadingSpinner />
                            : (unassigned.length > 0 && <LayoutsTable {...makeTableProps(unassigned)} />)
                        }
                    </div>
                ) : (
                    <LayoutsTable {...makeTableProps(unassigned)} />
                )}

                {folders.map(function(folder) {
                    var isOpen = openFolders.has(folder.id);
                    var isOver = dragOverId === folder.id;
                    return (
                        <div
                            key={folder.id}
                            style={isOver ? tintStyle : {}}
                            onDragOver={function(e) { e.preventDefault(); self.setState({ dragOverId: folder.id }); }}
                            onDragLeave={function(e) {
                                if (!e.currentTarget.contains(e.relatedTarget)) {
                                    self.setState({ dragOverId: null });
                                }
                            }}
                            onDrop={function(e) {
                                e.preventDefault();
                                self.setState({ dragOverId: null });
                                onDropToFolder && onDropToFolder(e, folder.id);
                            }}
                        >
                            <div
                                style={sectionHeaderStyle}
                                onClick={function() { self.toggleFolder(folder.id); }}
                            >
                                <span style={chevronStyle}>{isOpen ? '▾' : '▸'}</span>
                                <span style={sectionLabelStyle}>{folder.name}</span>
                                <span
                                    style={{ marginLeft: 'auto' }}
                                    onClick={function(e) { e.stopPropagation(); }}
                                >
                                    <TableButton
                                        marginRight
                                        icon="icon-edit"
                                        title="Renomear pasta"
                                        onClick={function() { onRenameFolder && onRenameFolder(folder); }}
                                    />
                                    <TableButton
                                        icon="icon-trash"
                                        title="Eliminar pasta"
                                        onClick={function() { onDeleteFolder && onDeleteFolder(folder); }}
                                    />
                                </span>
                            </div>
                            <div style={{ display: isOpen ? '' : 'none' }}>
                                {(isFoldersLoading || loadingGroups.has(folder.id))
                                    ? <LoadingSpinner />
                                    : grouped[folder.id].length === 0
                                        ? <p className="text-muted" style={{ padding: '8px 0', fontSize: '12px' }}>Pasta vazia</p>
                                        : <LayoutsTable {...makeTableProps(grouped[folder.id])} />
                                }
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
}
